/**
 * AI Contract Extraction Result Models — schema v2 (2026-04)
 *
 * Frontend types for the multi-stage extraction pipeline. These match the
 * backend response structure from extractContractDataEnhanced.
 *
 * v2 changes:
 *  - Added Anomaly + AnomalySeverity types
 *  - Added FieldMeta for per-field metadata in the review form
 *  - Added FieldEditorType for type-aware editor delegation
 *  - Added ContractTypeCandidate (ambiguity surfacing)
 *  - Expanded FIELD_LABELS to ~80 fields covering the full Contract domain
 *  - Added EnhancedExtractionResponse.schemaVersion to detect v1/v2 docs
 */

import { Contract } from '../../definitions/contract';

// ============================================================================
// CLASSIFICATION TYPES
// ============================================================================

/**
 * Contract category
 */
export type ContractCategory = 'residential' | 'commercial' | 'mixed' | 'other';

/**
 * Belgian contract type identifiers — full 13-type catalog
 */
export type BelgianContractTypeId =
  | 'residence_principale_9_years'
  | 'courte_duree'
  | 'longue_duree'
  | 'bail_etudiant'
  | 'colocation'
  | 'glissant'
  | 'renovation'
  | 'commercial_classique'
  | 'commercial_popup'
  | 'droit_commun'
  | 'rural'
  | 'emphyteotique'
  | 'superficie'
  /** Short-stay furnished letting (Airbnb / Booking) — not a residential lease. */
  | 'meuble_touristique'
  | 'unknown';

/**
 * Supported country codes
 */
export type SupportedCountryCode = 'BE' | 'FR' | 'NL' | 'LU' | 'DE' | 'unknown';

/**
 * Belgian regions
 */
export type BelgianRegion = 'Brussels' | 'Flanders' | 'Wallonia' | null;

/**
 * Type candidate — surfaces ambiguity when multiple types score >= 70
 */
export interface ContractTypeCandidate {
  typeId: BelgianContractTypeId;
  score: number;
  reasoning: string;
}

/**
 * Classification result from Stage 1
 */
export interface ContractClassification {
  contractTypeId: BelgianContractTypeId | string;
  contractTypeLabel: string;
  contractCategory: ContractCategory;
  confidence: number;
  candidateTypeIds?: ContractTypeCandidate[];
  countryCode: SupportedCountryCode;
  region: BelgianRegion;
  language: string;
  propertyType: string;
  keyIndicators: string[];
}

// ============================================================================
// ANOMALIES
// ============================================================================

export type AnomalySeverity = 'critical' | 'warning' | 'info';

export interface Anomaly {
  severity: AnomalySeverity;
  code: string;
  field: string;
  message: string;
  detectedValue?: unknown;
  expectedValue?: unknown;
  suggestedFix?: string;
  legalReference?: string;
  rawTextQuote?: string;
}

// ============================================================================
// EXTRACTION TYPES
// ============================================================================

/**
 * Field extraction priority
 */
export type ExtractionPriority = 'critical' | 'high' | 'medium' | 'low';

/**
 * Field category — extended to surface sub-domains in the UI
 */
export type FieldCategory =
  | 'core'
  | 'parties'
  | 'property'
  | 'dates'
  | 'financial'
  | 'legal'
  | 'countrySpecific';

/**
 * Source of extracted value
 */
export type ExtractionSource = 'explicit' | 'inferred' | 'default' | 'user';

/**
 * Field editor delegation type — picks the right Material control
 */
export type FieldEditorType =
  | 'text'
  | 'longText'
  | 'currency'
  | 'number'
  | 'date'
  | 'boolean'
  | 'enum'
  | 'tags'
  | 'iban';

/**
 * Single extracted field
 */
export interface ExtractedField {
  fieldName: string;
  value: unknown;
  confidence: number;
  source: ExtractionSource;
  rawText?: string;
  validationPassed: boolean;
  validationErrors?: string[];
  formControlPath?: string;
  /**
   * Optional display label supplied by the backend for dynamically-generated
   * fields (e.g. the per-tenant fields emitted for multi-tenant contracts)
   * that have no entry in the static FIELD_LABELS map. Preferred over the
   * static lookup when present.
   */
  label?: string;
  labelFr?: string;
}

/**
 * Extracted fields grouped by category
 */
export interface ExtractedFieldsByCategory {
  core: ExtractedField[];
  parties: ExtractedField[];
  property: ExtractedField[];
  dates: ExtractedField[];
  financial: ExtractedField[];
  legal: ExtractedField[];
  countrySpecific: ExtractedField[];
}

/**
 * Extraction summary statistics
 */
export interface ExtractionSummary {
  totalFieldsAttempted: number;
  totalFieldsExtracted: number;
  criticalFieldsExtracted: number;
  criticalFieldsMissing: number;
  highConfidenceFields: number;
  lowConfidenceFields: number;
  overallConfidence: number;
  missingRequiredFields: string[];
  lowConfidenceFieldNames: string[];
  warnings: string[];
  anomalies?: Anomaly[];
  canProceed: boolean;
}

/**
 * Processing metadata
 */
export interface ExtractionMetadata {
  stage1TokensUsed: number;
  stage2TokensUsed: number;
  totalTokensUsed: number;
  estimatedCost: number;
  totalProcessingTimeMs: number;
  classificationTimeMs: number;
  extractionTimeMs: number;
  classificationModel?: string;
  extractionModel?: string;
  /**
   * True when the backend served this result from the identical-document
   * cache. The timings and cost are then the ORIGINAL run's — the review
   * screen says "already computed" instead of quoting them as fresh.
   */
  fromCache?: boolean;
}

/**
 * Enhanced extraction response from backend
 */
export interface EnhancedExtractionResponse {
  success: boolean;
  schemaVersion?: 1 | 2;
  classification: ContractClassification;
  extractedFields: ExtractedFieldsByCategory;
  summary: ExtractionSummary;
  rawContract: Partial<Contract>;
  metadata: ExtractionMetadata;
  error?: string;
}

/**
 * Lifecycle of an async extraction job (workspaces/{wid}/extractionJobs/{jobId}).
 */
export type ExtractionJobStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';

/**
 * Async extraction job document — written by `startContractExtractionJob` and
 * `onExtractionJobCreated`, read in realtime by the add-contract screen to
 * drive the non-blocking progress card + resume-from-notification flow.
 */
export interface ExtractionJobDoc {
  jobId: string;
  status: ExtractionJobStatus;
  userId: string;
  workspaceId: string;
  propertyId: string;
  propertyType: string;
  /** Storage download URL of the uploaded source document (drives resume preview). */
  fileUrl: string;
  fileName: string;
  fileType: string;
  countryHint?: string | null;
  quickMode?: boolean;
  /**
   * Server timestamp stamped by `startContractExtractionJob`. Typed
   * structurally (rather than as a Firestore `Timestamp`) so this shared model
   * stays free of an SDK import; read it through `extractionJobCreatedAtMs`.
   */
  createdAt?: FirestoreTimestampLike | Date | string | null;
  /** Present once status === 'completed'. */
  result?: EnhancedExtractionResponse;
  /** Present once status === 'failed'. */
  error?: string;
  /**
   * Plain text the pipeline read from the document, capped server-side.
   * Feeds the review screen's left pane for file types with no viewer (.docx).
   */
  documentText?: string;
  /** How that text was obtained — `embedded-text`, `ocr`, … */
  documentTextSource?: string;
  /** Set on a job re-queued from a failed or completed one, pointing at the original. */
  retryOfJobId?: string;
  /**
   * First job of this document's retry chain. The free-re-run budget is counted
   * there rather than per-job, so forking two re-runs off the same review cannot
   * mint extra attempts (#362).
   */
  rootJobId?: string;
  /** Free AI re-runs already spent on this document. */
  freeRerunCount?: number;
  /**
   * Free re-runs left. Server-computed: the UI reads this rather than mirroring
   * the cap, which is what let `getAIImportLimit()`'s hardcoded 3 drift.
   * `undefined` on jobs predating #362 — treat as "allowed, count unknown" and
   * let the server refuse with `resource-exhausted` if it is not.
   */
  freeRerunsRemaining?: number;
  /** Set on a free re-run: the identical-document cache read is skipped. */
  bypassCache?: boolean;
  /** Set on a free re-run: both AI stages start on the stronger fallback model. */
  preferFallbackModel?: boolean;
  /** False when the job holds no import credit — a failure must not refund one. */
  creditReserved?: boolean;
  /**
   * Set by `cancelContractExtractionJob` (#383). The trigger polls this
   * between pipeline stages and unwinds to `status: 'cancelled'` once it
   * notices — the flag can be true for a short window while status still
   * reads `processing`, the last checkpoint the trigger reached before this
   * was set.
   */
  cancelRequested?: boolean;
  /** Present once status === 'cancelled'. */
  cancelledAt?: FirestoreTimestampLike | Date | string | null;
}

/** Structural shape of a Firestore `Timestamp` as it arrives on the client. */
export interface FirestoreTimestampLike {
  seconds: number;
  nanoseconds: number;
}

/**
 * Normalise `ExtractionJobDoc.createdAt` to epoch milliseconds for sorting.
 * Returns 0 when absent — a job whose server timestamp has not landed yet
 * (Firestore emits the local write before the server value resolves) sorts last
 * rather than throwing.
 */
export function extractionJobCreatedAtMs(job: Pick<ExtractionJobDoc, 'createdAt'>): number {
  const value = job.createdAt;
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return value.seconds * 1000;
  }
  return 0;
}

// ============================================================================
// UI DISPLAY TYPES
// ============================================================================

/**
 * Field extraction status for UI display
 */
export type FieldStatus = 'extracted' | 'missing' | 'low_confidence' | 'user_filled';

/**
 * Field display info for summary component
 */
export interface FieldDisplayInfo {
  fieldName: string;
  formControlPath: string;
  label: string;
  category: FieldCategory;
  status: FieldStatus;
  value: unknown;
  displayValue: string;
  confidence: number;
  isRequired: boolean;
  priority: ExtractionPriority;
  validationErrors?: string[];
  rawText?: string;
}

/**
 * Per-field metadata kept in a parallel signal map alongside the form value
 */
export interface FieldMeta {
  confidence: number;
  source: ExtractionSource;
  status: FieldStatus;
  rawText?: string;
  originalValue: unknown;
  anomalyCode?: string;
  isRequired: boolean;
}

/**
 * Category display info
 */
export interface CategoryDisplayInfo {
  category: FieldCategory;
  label: string;
  icon: string;
  fields: FieldDisplayInfo[];
  extractedCount: number;
  totalCount: number;
  missingRequiredCount: number;
}

/**
 * Extraction summary data for UI
 */
export interface ExtractionSummaryData {
  // Classification info
  classification: {
    contractTypeId: string;
    contractTypeLabel: string;
    countryCode: string;
    countryName: string;
    region: string | null;
    regionName: string | null;
    language: string;
    classificationConfidence: number;
    candidateTypeIds?: ContractTypeCandidate[];
    keyIndicators?: string[];
  };

  // Fields organized by category for display
  categories: CategoryDisplayInfo[];

  // Overall statistics
  statistics: {
    totalFields: number;
    extractedFields: number;
    missingRequired: number;
    lowConfidenceFields: number;
    overallConfidence: number;
  };

  // Warnings and status
  warnings: string[];
  anomalies: Anomaly[];
  canProceed: boolean;

  // Schema version (for v1/v2 branching in components)
  schemaVersion: 1 | 2;

  // Processing info
  processingInfo: {
    totalTimeMs: number;
    tokensUsed: number;
    estimatedCost: number;
    classificationModel?: string;
    extractionModel?: string;
    /** Served from the identical-document cache — the figures above are the
     *  original run's, not this one's. */
    fromCache?: boolean;
  };

  // Raw response (for prefill consumer + advanced usages)
  rawResponse?: EnhancedExtractionResponse;
}

/**
 * User update for a field in the summary step
 */
export interface SummaryFieldUpdate {
  fieldName: string;
  formControlPath: string;
  value: unknown;
  source: 'user';
}

/**
 * Payload emitted by the new extraction-summary component on Continue.
 * Replaces the legacy SummaryFieldUpdate map; consumers can still derive
 * a map from `userUpdates` if needed for backwards compatibility.
 */
export interface ExtractionProceedPayload {
  /** Flat key/value map of the user-edited form (path → value) */
  formValue: Record<string, unknown>;
  /** Per-field metadata snapshot (path → meta) */
  fieldMeta: Record<string, FieldMeta>;
  /** Final chosen classification (after any user override) */
  classification: ContractClassification;
  /** Anomalies the user explicitly acknowledged or resolved */
  resolvedAnomalies: string[];
  /** Legacy map for older consumers (path → SummaryFieldUpdate) */
  userUpdates: Map<string, SummaryFieldUpdate>;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get human-readable country name
 */
export function getCountryName(countryCode: SupportedCountryCode): string {
  const names: Record<SupportedCountryCode, string> = {
    'BE': 'Belgium',
    'FR': 'France',
    'NL': 'Netherlands',
    'LU': 'Luxembourg',
    'DE': 'Germany',
    'unknown': 'Unknown'
  };
  return names[countryCode] || countryCode;
}

/**
 * Get human-readable region name
 */
export function getRegionName(region: BelgianRegion): string | null {
  if (!region) return null;
  const names: Record<string, string> = {
    'Brussels': 'Brussels-Capital Region',
    'Flanders': 'Flanders',
    'Wallonia': 'Wallonia'
  };
  return names[region] || region;
}

/**
 * Get category label
 */
export function getCategoryLabel(category: FieldCategory): string {
  const labels: Record<FieldCategory, string> = {
    'core': 'Basic Information',
    'parties': 'Parties (Owner & Tenant)',
    'property': 'Property Details',
    'dates': 'Contract Dates',
    'financial': 'Financial Terms',
    'legal': 'Legal Clauses',
    'countrySpecific': 'Belgian Specifics'
  };
  return labels[category] || category;
}

/**
 * Get category icon
 */
export function getCategoryIcon(category: FieldCategory): string {
  const icons: Record<FieldCategory, string> = {
    'core': 'description',
    'parties': 'people',
    'property': 'home',
    'dates': 'event',
    'financial': 'euro',
    'legal': 'gavel',
    'countrySpecific': 'flag'
  };
  return icons[category] || 'help';
}

/**
 * Get field status from extraction data
 */
export function getFieldStatus(field: ExtractedField, _isRequired: boolean): FieldStatus {
  if (field.source === 'user') {
    return 'user_filled';
  }
  if (field.value == null) {
    return 'missing';
  }
  if (field.confidence < 60) {
    return 'low_confidence';
  }
  return 'extracted';
}

/**
 * Get confidence CSS class
 */
export function getConfidenceClass(confidence: number): string {
  if (confidence >= 80) return 'confidence-high';
  if (confidence >= 60) return 'confidence-medium';
  if (confidence > 0) return 'confidence-low';
  return 'confidence-none';
}

/**
 * Get status icon
 */
export function getStatusIcon(status: FieldStatus): string {
  switch (status) {
    case 'extracted': return 'check_circle';
    case 'missing': return 'error';
    case 'low_confidence': return 'warning';
    case 'user_filled': return 'edit';
    default: return 'help';
  }
}

/**
 * Format value for display
 */
export function formatDisplayValue(value: unknown, fieldName: string): string {
  if (value == null) return '—';

  // Date fields
  if (fieldName.toLowerCase().includes('date') && value) {
    try {
      const date = new Date(value as string | number);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
    } catch {
      return String(value);
    }
  }

  // Currency/amount fields — use the editor-type registry as the source of
  // truth so plain-number fields (e.g. depositMonths, rentPaymentDay) are not
  // formatted with a € prefix just because their name contains "deposit"/"rent".
  if (getFieldEditorType(fieldName) === 'currency' && typeof value === 'number') {
    return `€${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // Boolean fields
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Arrays
  if (Array.isArray(value)) {
    return value.join(', ') || '—';
  }

  return String(value);
}

/**
 * Field labels — schema v2 with ~80 fields covering full Contract domain.
 * Used by the review UI to render human-readable labels per field.
 */
export const FIELD_LABELS: Record<string, string> = {
  // Owner
  ownerName: 'Owner Name',
  ownerAddress: 'Owner Address',
  ownerEmail: 'Owner Email',
  ownerPhone: 'Owner Phone',
  ownerIban: 'Owner IBAN',
  ownerNationalRegistryNumber: 'Owner NRN',
  ownerCompanyNumber: 'Owner Company Number (BCE)',
  ownerLegalForm: 'Owner Legal Form',
  // Tenants
  tenantName: 'Primary Tenant Name',
  tenantNames: 'All Tenant Names',
  tenantEmail: 'Tenant Email',
  tenantPhone: 'Tenant Phone',
  tenantNationalRegistryNumber: 'Tenant NRN',
  tenantDateOfBirth: 'Tenant Date of Birth',
  // Property
  propertyAddress: 'Property Address',
  propertyStreet: 'Street',
  propertyNumber: 'Number',
  propertyBox: 'Box',
  propertyPostalCode: 'Postal Code',
  propertyCity: 'City',
  propertyDescription: 'Property Description',
  propertyType: 'Property Type',
  propertySurfaceArea: 'Surface Area (m²)',
  propertyNumberOfRooms: 'Rooms',
  propertyNumberOfBedrooms: 'Bedrooms',
  propertyFurnished: 'Furnished',
  propertyHasGarden: 'Has Garden',
  propertyHasParking: 'Has Parking',
  propertyCadastralReference: 'Cadastral Reference',
  propertyConstructionYear: 'Year Built',
  epcCertificateNumber: 'EPC Certificate Number',
  epcRating: 'EPC Rating',
  epcIssueDate: 'EPC Issue Date',
  epcExpiryDate: 'EPC Expiry Date',
  epcEmissionValue: 'EPC Emission (kWh/m²/yr)',
  electricalCertificateNumber: 'Electrical Compliance Certificate',
  // Dates
  signatureDate: 'Signature Date',
  startDate: 'Start Date',
  endDate: 'End Date',
  durationMonths: 'Duration (months)',
  isAutoRenewable: 'Auto-Renewable',
  renewalTerm: 'Renewal Term (months)',
  tenantNoticePeriodMonths: 'Tenant Notice Period (months)',
  landlordNoticePeriodMonths: 'Landlord Notice Period (months)',
  renewalNoticePeriodMonths: 'Renewal Notice Period (months)',
  registrationDate: 'Registration Date',
  registrationDeadline: 'Registration Deadline',
  inventoryDate: 'Inventory Date',
  inventoryConductedBy: 'Inventory Conducted By',
  academicYearStart: 'Academic Year Start',
  academicYearEnd: 'Academic Year End',
  // Financial — rent
  monthlyRent: 'Monthly Base Rent',
  currency: 'Currency',
  rentFrequency: 'Rent Frequency',
  rentPaymentDay: 'Rent Payment Day',
  firstPaymentDate: 'First Payment Date',
  // Financial — charges
  chargesAmount: 'Monthly Charges',
  chargesType: 'Charges Type',
  provisionAmount: 'Provision Amount',
  chargesIncludeWater: 'Charges Include Water',
  chargesIncludeHeating: 'Charges Include Heating',
  chargesIncludeElectricity: 'Charges Include Electricity',
  chargesIncludeCommonAreas: 'Charges Include Common Areas',
  chargesIncludeInternet: 'Charges Include Internet',
  // Financial — deposit
  depositAmount: 'Security Deposit',
  depositMonths: 'Deposit (months)',
  depositType: 'Deposit Type',
  depositBlockedAccountNumber: 'Blocked Account IBAN',
  depositBankName: 'Deposit Bank',
  depositPaymentDate: 'Deposit Payment Date',
  // Financial — indexation
  indexationEnabled: 'Indexation Enabled',
  indexationType: 'Indexation Type',
  indexationFrequency: 'Indexation Frequency',
  baseIndexValue: 'Base Index Value',
  baseIndexMonth: 'Base Index Month',
  baseIndexYear: 'Base Index Year',
  // Financial — special / tax
  hasSpecialRentClauses: 'Has Special Rent Clauses',
  registrationFees: 'Registration Fees',
  registrationFeePaidBy: 'Registration Fees Paid By',
  witholdingTaxRate: 'Withholding Tax Rate (%)',
  parentalGuaranteeProvided: 'Parental Guarantee',
  parentalGuaranteeAmount: 'Parental Guarantee Amount',
  // Legal / clauses
  petsAllowed: 'Pets Allowed',
  maxPets: 'Max Pets',
  allowedPetTypes: 'Allowed Pet Types',
  smokingAllowed: 'Smoking Allowed',
  sublettingAllowed: 'Subletting Allowed',
  assignmentAllowed: 'Assignment Allowed',
  maximumOccupants: 'Maximum Occupants',
  businessUseAllowed: 'Business Use Allowed',
  renovationsAllowed: 'Renovations Allowed',
  gardenMaintenance: 'Garden Maintenance',
  keyDepositAmount: 'Key Deposit Amount',
  numberOfKeySets: 'Number of Key Sets',
  earlyTerminationAllowed: 'Early Termination Allowed',
  earlyTerminationConditions: 'Early Termination Conditions',
  applicableLaw: 'Applicable Law',
  jurisdiction: 'Jurisdiction',
  customClauses: 'Custom Clauses',
  // Country specific (Belgian)
  region: 'Belgian Region',
  isRegistered: 'Contract Registered',
  registrationNumber: 'Registration Number',
  hasInventory: 'Has Inventory (état des lieux)',
  hasElectricalComplianceCertificate: 'Electrical Compliance',
  pacteColocationSigned: 'Pacte de colocation Signed',
  pacteColocationDate: 'Pacte de colocation Date',
  solidaryLiability: 'Solidary Liability',
  tenantReplacementAllowed: 'Tenant Replacement Allowed',
  studentProofRequired: 'Student Proof Required',
  studentInstitution: 'Student Institution',
  commercialActivity: 'Commercial Activity',
  allowedActivities: 'Allowed Activities',
  triennialBreakRights: 'Triennial Break Rights',
  renewalRights: 'Renewal Rights',
  keyMoney: 'Key Money / Pas de porte',
  canon: 'Canon (annual)',
  canonRevisionClause: 'Canon Revision Clause',
  constructionRights: 'Construction Rights',
  agriculturalType: 'Agricultural Type',
  cropAllowed: 'Crops Allowed',
  aisName: 'AIS / Social Intermediary',
  renovationDescription: 'Renovation Description',
  renovationStartDate: 'Renovation Start',
  renovationEndDate: 'Renovation End',
  rentReductionDuringWorks: 'Rent Reduction During Works',
  popupActivity: 'Pop-up Activity',
  usageRestrictions: 'Usage Restrictions'
};

/**
 * Field priorities (used for sorting + weight in confidence calculation)
 */
export const FIELD_PRIORITIES: Record<string, ExtractionPriority> = {
  // Critical
  ownerName: 'critical',
  tenantName: 'critical',
  propertyAddress: 'critical',
  startDate: 'critical',
  endDate: 'critical',
  monthlyRent: 'critical',
  depositAmount: 'critical',
  // High
  signatureDate: 'high',
  durationMonths: 'high',
  ownerAddress: 'high',
  ownerIban: 'high',
  tenantNames: 'high',
  propertyStreet: 'high',
  propertyPostalCode: 'high',
  propertyCity: 'high',
  propertyType: 'high',
  chargesAmount: 'high',
  chargesType: 'high',
  depositMonths: 'high',
  indexationEnabled: 'high',
  region: 'high',
  pacteColocationSigned: 'high',
  solidaryLiability: 'high',
  commercialActivity: 'high',
  triennialBreakRights: 'high',
  canon: 'high',
  constructionRights: 'high',
  // Medium
  tenantEmail: 'medium',
  tenantPhone: 'medium',
  ownerEmail: 'medium',
  ownerPhone: 'medium',
  propertySurfaceArea: 'medium',
  propertyNumberOfRooms: 'medium',
  propertyNumberOfBedrooms: 'medium',
  propertyFurnished: 'medium',
  epcCertificateNumber: 'medium',
  epcRating: 'medium',
  isAutoRenewable: 'medium',
  renewalTerm: 'medium',
  tenantNoticePeriodMonths: 'medium',
  landlordNoticePeriodMonths: 'medium',
  registrationDate: 'medium',
  inventoryDate: 'medium',
  rentFrequency: 'medium',
  rentPaymentDay: 'medium',
  provisionAmount: 'medium',
  depositType: 'medium',
  indexationType: 'medium',
  baseIndexValue: 'medium',
  hasSpecialRentClauses: 'medium',
  petsAllowed: 'medium',
  sublettingAllowed: 'medium',
  earlyTerminationAllowed: 'medium',
  isRegistered: 'medium',
  hasInventory: 'medium',
  customClauses: 'medium',
  academicYearStart: 'medium',
  academicYearEnd: 'medium',
  parentalGuaranteeProvided: 'medium',
  tenantReplacementAllowed: 'medium',
  renewalRights: 'medium',
  keyMoney: 'medium',
  renovationStartDate: 'medium',
  renovationEndDate: 'medium',
  rentReductionDuringWorks: 'medium',
  agriculturalType: 'medium',
  canonRevisionClause: 'medium',
  // Low (everything else defaults to low if not listed)
};

/**
 * Required fields (block import until filled)
 */
export const REQUIRED_FIELDS: string[] = [
  'ownerName',
  'tenantName',
  'propertyAddress',
  'startDate',
  'monthlyRent',
  'depositAmount'
];

/**
 * Field type registry — drives the field-editor delegation in the review UI
 */
export const FIELD_EDITOR_TYPES: Record<string, FieldEditorType> = {
  // Currency
  monthlyRent: 'currency',
  depositAmount: 'currency',
  chargesAmount: 'currency',
  provisionAmount: 'currency',
  registrationFees: 'currency',
  parentalGuaranteeAmount: 'currency',
  keyDepositAmount: 'currency',
  keyMoney: 'currency',
  canon: 'currency',
  // Number
  durationMonths: 'number',
  depositMonths: 'number',
  baseIndexValue: 'number',
  baseIndexMonth: 'number',
  baseIndexYear: 'number',
  rentPaymentDay: 'number',
  renewalTerm: 'number',
  tenantNoticePeriodMonths: 'number',
  landlordNoticePeriodMonths: 'number',
  renewalNoticePeriodMonths: 'number',
  propertySurfaceArea: 'number',
  propertyNumberOfRooms: 'number',
  propertyNumberOfBedrooms: 'number',
  propertyConstructionYear: 'number',
  maxPets: 'number',
  maximumOccupants: 'number',
  numberOfKeySets: 'number',
  witholdingTaxRate: 'number',
  epcEmissionValue: 'number',
  renewalRights: 'number',
  // Date
  signatureDate: 'date',
  startDate: 'date',
  endDate: 'date',
  registrationDate: 'date',
  registrationDeadline: 'date',
  inventoryDate: 'date',
  academicYearStart: 'date',
  academicYearEnd: 'date',
  pacteColocationDate: 'date',
  epcIssueDate: 'date',
  epcExpiryDate: 'date',
  firstPaymentDate: 'date',
  depositPaymentDate: 'date',
  tenantDateOfBirth: 'date',
  renovationStartDate: 'date',
  renovationEndDate: 'date',
  // Boolean
  isAutoRenewable: 'boolean',
  indexationEnabled: 'boolean',
  hasSpecialRentClauses: 'boolean',
  parentalGuaranteeProvided: 'boolean',
  petsAllowed: 'boolean',
  smokingAllowed: 'boolean',
  sublettingAllowed: 'boolean',
  assignmentAllowed: 'boolean',
  businessUseAllowed: 'boolean',
  renovationsAllowed: 'boolean',
  earlyTerminationAllowed: 'boolean',
  isRegistered: 'boolean',
  hasInventory: 'boolean',
  hasElectricalComplianceCertificate: 'boolean',
  pacteColocationSigned: 'boolean',
  solidaryLiability: 'boolean',
  tenantReplacementAllowed: 'boolean',
  studentProofRequired: 'boolean',
  triennialBreakRights: 'boolean',
  constructionRights: 'boolean',
  rentReductionDuringWorks: 'boolean',
  propertyFurnished: 'boolean',
  propertyHasGarden: 'boolean',
  propertyHasParking: 'boolean',
  chargesIncludeWater: 'boolean',
  chargesIncludeHeating: 'boolean',
  chargesIncludeElectricity: 'boolean',
  chargesIncludeCommonAreas: 'boolean',
  chargesIncludeInternet: 'boolean',
  // Enum
  chargesType: 'enum',
  depositType: 'enum',
  indexationType: 'enum',
  region: 'enum',
  epcRating: 'enum',
  rentFrequency: 'enum',
  registrationFeePaidBy: 'enum',
  gardenMaintenance: 'enum',
  propertyType: 'enum',
  // IBAN
  ownerIban: 'iban',
  depositBlockedAccountNumber: 'iban',
  // Tags / array
  tenantNames: 'tags',
  allowedPetTypes: 'tags',
  allowedActivities: 'tags',
  // Long text
  customClauses: 'longText',
  earlyTerminationConditions: 'longText',
  renovationDescription: 'longText',
  usageRestrictions: 'longText',
  applicableLaw: 'longText',
  jurisdiction: 'longText'
};

/**
 * Get effective editor type, defaulting to 'text'
 */
export function getFieldEditorType(fieldName: string): FieldEditorType {
  const mapped = FIELD_EDITOR_TYPES[fieldName];
  if (mapped) return mapped;
  // Dynamic per-tenant fields (e.g. `tenant2DateOfBirth`) aren't in the static
  // map — infer the editor from the suffix so dates still get a date picker.
  if (/^tenant\d+/.test(fieldName) && /DateOfBirth$/.test(fieldName)) return 'date';
  return 'text';
}

/**
 * Get effective priority, defaulting to 'low'
 */
export function getFieldPriority(fieldName: string): ExtractionPriority {
  return FIELD_PRIORITIES[fieldName] || 'low';
}

/**
 * Transform backend response to UI summary data
 */
export function transformToSummaryData(response: EnhancedExtractionResponse): ExtractionSummaryData {
  // Create category display info
  const categoryOrder: FieldCategory[] = ['parties', 'property', 'dates', 'financial', 'legal', 'countrySpecific'];

  const categories: CategoryDisplayInfo[] = categoryOrder.map(category => {
    const categoryFields = response.extractedFields[category] || [];
    const fieldDisplayInfos: FieldDisplayInfo[] = categoryFields.map(field => ({
      fieldName: field.fieldName,
      formControlPath: field.formControlPath || field.fieldName,
      label: field.label || FIELD_LABELS[field.fieldName] || field.fieldName,
      category,
      status: getFieldStatus(field, REQUIRED_FIELDS.includes(field.fieldName)),
      value: field.value,
      displayValue: formatDisplayValue(field.value, field.fieldName),
      confidence: field.confidence,
      isRequired: REQUIRED_FIELDS.includes(field.fieldName),
      priority: getFieldPriority(field.fieldName),
      validationErrors: field.validationErrors,
      rawText: field.rawText
    }));

    // Sort by priority then by required
    fieldDisplayInfos.sort((a, b) => {
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      if (a.isRequired !== b.isRequired) {
        return a.isRequired ? -1 : 1;
      }
      return 0;
    });

    return {
      category,
      label: getCategoryLabel(category),
      icon: getCategoryIcon(category),
      fields: fieldDisplayInfos,
      extractedCount: fieldDisplayInfos.filter(f => f.status === 'extracted' || f.status === 'low_confidence').length,
      totalCount: fieldDisplayInfos.length,
      missingRequiredCount: fieldDisplayInfos.filter(f => f.status === 'missing' && f.isRequired).length
    };
  });

  return {
    classification: {
      contractTypeId: response.classification.contractTypeId,
      contractTypeLabel: response.classification.contractTypeLabel,
      countryCode: response.classification.countryCode,
      countryName: getCountryName(response.classification.countryCode),
      region: response.classification.region,
      regionName: getRegionName(response.classification.region),
      language: response.classification.language,
      classificationConfidence: response.classification.confidence,
      candidateTypeIds: response.classification.candidateTypeIds,
      keyIndicators: response.classification.keyIndicators
    },
    categories,
    statistics: {
      totalFields: response.summary.totalFieldsAttempted,
      extractedFields: response.summary.totalFieldsExtracted,
      missingRequired: response.summary.missingRequiredFields.length,
      lowConfidenceFields: response.summary.lowConfidenceFields,
      overallConfidence: response.summary.overallConfidence
    },
    warnings: response.summary.warnings,
    anomalies: response.summary.anomalies || [],
    canProceed: response.summary.canProceed,
    schemaVersion: response.schemaVersion ?? 1,
    processingInfo: {
      totalTimeMs: response.metadata.totalProcessingTimeMs,
      tokensUsed: response.metadata.totalTokensUsed,
      estimatedCost: response.metadata.estimatedCost,
      classificationModel: response.metadata.classificationModel,
      extractionModel: response.metadata.extractionModel,
      fromCache: response.metadata.fromCache
    },
    rawResponse: response
  };
}

/**
 * Belgian contract type labels (for type-override dropdown in review UI)
 */
export const BELGIAN_CONTRACT_TYPE_LABELS: Record<BelgianContractTypeId, string> = {
  'residence_principale_9_years': 'Bail de résidence principale (9 ans)',
  'courte_duree': 'Bail de courte durée',
  'longue_duree': 'Bail de longue durée',
  'bail_etudiant': 'Bail étudiant',
  'colocation': 'Bail de colocation',
  'glissant': 'Bail glissant',
  'renovation': 'Bail à rénovation',
  'commercial_classique': 'Bail commercial classique',
  'commercial_popup': 'Bail commercial pop-up',
  'droit_commun': 'Bail de droit commun',
  'rural': 'Bail à ferme',
  'emphyteotique': 'Bail emphytéotique',
  'superficie': 'Droit de superficie',
  'meuble_touristique': 'Location meublée de tourisme',
  'unknown': 'Type inconnu'
};
